import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { CompanyInfoContext } from "../../context/LegacyContextBridge";

export interface BrandProps {
  /** Optional navigation target. When provided, the brand is wrapped in a Link */
  to?: string;
  /** Controls sidebar expand/collapse animation for the brand text (default: true) */
  isExpanded?: boolean;
  /** Sizing presets: 'sm' (dashboard sidebar), 'md' (standard navbar, default), 'lg' (auth pages / hero) */
  size?: "sm" | "md" | "lg";
  /** Color theme: 'dark' (dark text for light backgrounds, default) or 'light' (white text for dark/glass backgrounds) */
  theme?: "dark" | "light";
  /** Optional company info object override (otherwise consumes CompanyInfoContext) */
  companyInfo?: any;
  /** Primary brand name override (defaults to "Codehub") */
  title?: string;
  /** Secondary subtitle override (defaults to "Technologies Ltd") */
  subtitle?: string;
  /** Fallback initials to display if no logo is available (default: "CH") */
  fallbackInitials?: string;
  /** Optional additional CSS class for the root container */
  className?: string;
  /** Optional click handler */
  onClick?: () => void;
}

export const Brand: React.FC<BrandProps> = ({
  to,
  isExpanded = true,
  size = "md",
  theme = "dark",
  companyInfo: propCompanyInfo,
  title,
  subtitle,
  fallbackInitials = "CH",
  className = "",
  onClick,
}) => {
  const contextCompanyInfo = useContext(CompanyInfoContext) || {};
  const companyInfo = propCompanyInfo || contextCompanyInfo;

  const resolvedTitle =
    title || companyInfo?.company_name?.split(" ")[0] || "Codehub";
  const resolvedSubtitle =
    subtitle ||
    companyInfo?.company_name?.split(" ").slice(1).join(" ") ||
    "Technologies Ltd";

  const logoSizes = {
    sm: "cht-w-8 cht-h-8",
    md: "cht-w-9 cht-h-9 sm:cht-w-10 sm:cht-h-10",
    lg: "cht-w-10 cht-h-10 sm:cht-w-12 sm:cht-h-12",
  };

  const titleSizes = {
    sm: "24px",
    md: "28px",
    lg: "32px",
  };

  const subtitleSizes = {
    sm: "8.5px",
    md: "9px",
    lg: "10px",
  };

  const isLight = theme === "light";

  const brandContent = (
    <div className={`cht-flex cht-items-center cht-gap-3 cht-min-w-0 ${className}`}>
      {/* Brand Logo / Fallback Badge */}
      {companyInfo?.get_logo ? (
        <div
          className={`cht-relative ${logoSizes[size]} cht-rounded-xl cht-overflow-hidden cht-p-1 ${
            isLight
              ? "cht-bg-white/90 cht-border cht-border-white/40 cht-shadow-md"
              : "cht-bg-white/80 cht-shadow-sm cht-border cht-border-gray-200/50"
          } group-hover:cht-scale-105 cht-transition-transform cht-duration-200 cht-flex-shrink-0`}
        >
          <img
            src={companyInfo.get_logo}
            alt={`${companyInfo?.company_name || resolvedTitle}'s Logo`}
            className="cht-w-full cht-h-full cht-object-contain"
          />
        </div>
      ) : (
        <div
          className={`cht-flex-shrink-0 ${logoSizes[size]} cht-rounded-xl ${
            isLight
              ? "cht-bg-white/20 cht-backdrop-blur-md cht-border cht-border-white/30 cht-shadow-md"
              : "cht-bg-[#3e57da] cht-shadow-md"
          } cht-flex cht-items-center cht-justify-center group-hover:cht-scale-105 cht-transition-transform cht-duration-200`}
        >
          <span className="cht-text-white cht-font-bold cht-text-[11px] cht-tracking-tight">
            {fallbackInitials}
          </span>
        </div>
      )}

      {/* Brand Text with Expand / Collapse Transition */}
      <div
        style={{
          opacity: isExpanded ? 1 : 0,
          width: isExpanded ? "auto" : 0,
          overflow: "hidden",
          transition: "opacity 0.25s ease, width 0.3s ease",
          whiteSpace: "nowrap",
        }}
      >
        <div
          className={`cht-flex cht-flex-col ${
            isLight ? "cht-text-white" : "cht-text-[#1e293b]"
          }`}
          style={{ lineHeight: "1" }}
        >
          <span
            className="fw-bolder"
            style={{
              fontSize: titleSizes[size],
              letterSpacing: "-0.5px",
            }}
          >
            {resolvedTitle}
          </span>
          <span
            className={`fw-bold text-uppercase ${
              isLight ? "cht-text-white/80" : ""
            }`}
            style={{
              fontSize: subtitleSizes[size],
              letterSpacing: "2px",
            }}
          >
            {resolvedSubtitle}
          </span>
        </div>
      </div>
    </div>
  );

  if (to) {
    return (
      <Link
        to={to}
        onClick={onClick}
        className="cht-inline-flex cht-items-center cht-no-underline cht-group"
      >
        {brandContent}
      </Link>
    );
  }

  return brandContent;
};

export default Brand;
